import React, { useContext, useState } from 'react'
import { ModalActions } from '../../common/modal/Modal'
import { Reorder } from 'motion/react'
import { RiskAnalysis, SWOT } from '../../../api/schemas/schema'
import { Button, Divider, makeStyles, Text, tokens } from '@fluentui/react-components'
import { SwotTypeBadge } from '../RiskBadges'
import { ReOrderDotsVertical20Regular } from '@fluentui/react-icons'
import { t } from '../../../i18n/i18n'
import { changeOrderOfSwots, groupSWOTsByType } from '../../../helpers/swotHelper'
import { DataContext } from '../../../context/DataContext'
import { IItemState } from '../../../interfaces/IItemState'
import { DraggableModal } from '../../common/modal/DraggableModal'

const useStyles = makeStyles({
   groupContainer: {
      marginBottom: tokens.spacingVerticalXXL,
   },
   groupHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: tokens.spacingVerticalL,
   },
   reorderItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: tokens.spacingHorizontalM,
      border: `1px solid ${tokens.colorBrandStroke2}`,
      borderRadius: tokens.borderRadiusXLarge,
      marginBottom: tokens.spacingVerticalS,
      backgroundColor: tokens.colorBrandBackgroundInverted,
      cursor: 'grab',
   },
   noListStyle: {
      listStyle: 'none',
      padding: 0,
   },
   noPadding: {
      padding: 0,
   },
   divider: {
      marginBottom: tokens.spacingVerticalM,
   },
   textSpacer: {
      display: 'flex',
      gap: tokens.spacingHorizontalXL,
   },
})

type ModalSWOTChangeOrderProps = {
   open: boolean
   close: () => void
   analysis: RiskAnalysis
   items: SWOT[]
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
}

export const ModalSWOTChangeOrder = ({ open, close, analysis, items, setRiskAnalysisState }: ModalSWOTChangeOrderProps) => {
   const dataContext = useContext(DataContext)
   const classes = useStyles()
   const [isDirty, setIsDirty] = useState(false)
   const [isSubmitting, setIsSubmitting] = useState(false)
   const [groupedItems, setGroupedItems] = useState(() => groupSWOTsByType(items))

   const handleReorder = (groupKey: string, newOrder: SWOT[]) => {
      setGroupedItems((prev) => {
         const updatedGroup = newOrder.map((item, index) => ({
            ...item,
            order: index + 1,
         }))

         const updatedGroups = {
            ...prev,
            [groupKey]: updatedGroup,
         }

         setIsDirty(true)

         return updatedGroups
      })
   }

   const handleClose = () => {
      close()
      setIsSubmitting(false)
      setIsDirty(false)
   }

   const handleSave = () => {
      setIsSubmitting(true)
      const updatedItems = Object.values(groupedItems).flat()
      
      changeOrderOfSwots(dataContext, setRiskAnalysisState, analysis, updatedItems)
      close()

      setIsSubmitting(false)
      setIsDirty(false)
   }

   return (
      <DraggableModal title={t('ValueInOrderOfImportance')} open={open} onClose={close} noDivider>
         <>
            {Object.entries(groupedItems).map(([groupKey, groupItems]) => (
               <div key={groupKey} className={classes.groupContainer}>
                  <Divider className={classes.divider} />
                  <div className={classes.groupHeader}>
                     <SwotTypeBadge type={groupItems[0].type} />
                  </div>

                  <Reorder.Group axis="y" values={groupItems} onReorder={(newOrder) => handleReorder(groupKey, newOrder)} className={classes.noPadding}>
                     {groupItems.map((item) => (
                        <Reorder.Item key={item.id} value={item} className={classes.noListStyle}>
                           <div className={classes.reorderItem}>
                              <div className={classes.textSpacer}>
                                 <Text weight="semibold">{item.order}</Text>
                                 <Text>{item.title}</Text>
                              </div>
                              <ReOrderDotsVertical20Regular />
                           </div>
                        </Reorder.Item>
                     ))}
                  </Reorder.Group>
               </div>
            ))}
            <Divider className={classes.divider} />
            <ModalActions>
               <Button onClick={handleClose} disabled={isSubmitting}>
                  {t('Cancel')}
               </Button>
               <Button onClick={handleSave} type="submit" appearance="primary" disabled={!isDirty || isSubmitting}>
                  {t('Save')}
               </Button>
            </ModalActions>
         </>
      </DraggableModal>
   )
}
